import React, { useEffect, type SyntheticEvent } from 'react';
import Button from '@atlaskit/button';
import BackIcon from '@atlaskit/icon/core/migration/arrow-left--arrow-left-circle';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import FullScreenOffButton from '@atlaskit/icon/core/migration/shrink-diagonal--vid-full-screen-off';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useEocFeedbackCollector } from '@atlassian/jira-eoc-feedback-collector/src/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { FeedbackCollectorDescription } from '../../common/ui/feedback-collector-description/index.tsx';
import { useProgressActions, useProgressStore } from '../../controllers/store/progress/index.tsx';
import { ProgressContents } from '../../controllers/store/progress/types.tsx';
import { AlertsStep } from './alerts-step/index.tsx';
import { AutomateAlertAndIncidentResponseStep } from './automate-alert-and-incident-response-step/index.tsx';
import { ChatToolsStep } from './chat-tools-step/index.tsx';
import { DeprecatedOnesStep } from './deprecated-ones-step/index.tsx';
import { FindStep } from './find-step/index.tsx';
import { HeartbeatsStep } from './heartbeats-step/index.tsx';
import { IncidentsStep } from './incidents-step/index.tsx';
import { IntegrationsStep } from './integrations-step/index.tsx';
import { TransitionHomeLeft } from './left/index.tsx';
import messages from './messages.tsx';
import { NotificationsStep } from './notifications-step/index.tsx';
import { TransitionHomeRight } from './right/index.tsx';
import { TeamsConceptStep } from './teams-concept-step/index.tsx';
import { UsersAndRolesStep } from './users-and-roles-step/index.tsx';

type TransitionHomeModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const GiveFeedbackButton = ({ onClose }: { onClose: () => void }) => {
	const { formatMessage } = useIntl();
	const { openEocFeedbackCollector } = useEocFeedbackCollector();

	const onFeedbackButtonClick = (_: SyntheticEvent, analyticEvent: UIAnalyticsEvent) => {
		onClose();
		fireUIAnalytics(analyticEvent, 'eocGiveFeedbackButton');
		openEocFeedbackCollector({
			showTypeField: true,
			renderCustomContent: FeedbackCollectorDescription,
			feedbackTitle: formatMessage(messages.giveFeedbackButtonNonFinal),
		});
	};

	return (
		<Box xcss={giveFeedbackBoxStyles}>
			<Button
				appearance="subtle"
				iconBefore={
					<FeedbackIcon
						spacing="spacious"
						label={formatMessage(messages.giveFeedbackButtonNonFinal)}
						color={token('color.icon.subtle')}
					/>
				}
				aria-label={formatMessage(messages.giveFeedbackButtonNonFinal)}
				onClick={onFeedbackButtonClick}
			/>
		</Box>
	);
};

const MinimizeButton = ({ onClose }: { onClose: () => void }) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={closeFullScreenBoxStyles}>
			<Button
				appearance="subtle"
				iconBefore={
					<FullScreenOffButton
						spacing="spacious"
						label={formatMessage(messages.minimizeNonFinal)}
						color={token('color.icon.subtle')}
					/>
				}
				aria-label={formatMessage(messages.closeButtonNonFinal)}
				onClick={(_, analyticEvent) => {
					fireUIAnalytics(analyticEvent, 'closetransitionHome');
					onClose();
				}}
			/>
		</Box>
	);
};

export const TransitionHomeModal = ({ isOpen, onClose }: TransitionHomeModalProps) => {
	const [{ activeContent, activeTask }] = useProgressStore();
	const { backToHome } = useProgressActions();

	useEffect(() => {
		if (activeTask || activeTask === 0) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const nodes = document.querySelectorAll('.transition-home-content-box');
			const node = nodes[activeTask];

			if (node) {
				node.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	}, [activeContent, activeTask]);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={(e, analyticEvent) => {
						fireUIAnalytics(analyticEvent, 'transitionHome');
						onClose();
					}}
					width={1100}
					height={700}
					messageId="eoc-changeboarding.ui.transition-home.modal"
					messageType="transactional"
				>
					<FireScreenAnalytics />
					<Inline grow="fill" xcss={inlineStyles}>
						{activeContent === ProgressContents.Home ? (
							<>
								<TransitionHomeLeft />
								<TransitionHomeRight />
								<GiveFeedbackButton onClose={onClose} />
								<MinimizeButton onClose={onClose} />
							</>
						) : (
							<>
								<Box xcss={stepLeftBoxStyles} padding="space.250">
									<Button
										appearance="subtle"
										iconBefore={<BackIcon label="" />}
										onClick={() => backToHome(activeContent)}
									>
										<FormattedMessage {...messages.backButtonNonFinal} />
									</Button>
								</Box>
								<Box padding="space.300" xcss={stepsWrapperBoxStyles}>
									{activeContent === ProgressContents.UsersAndRoles && <UsersAndRolesStep />}
									{activeContent === ProgressContents.Alerts && <AlertsStep />}
									{activeContent === ProgressContents.Notifications && <NotificationsStep />}
									{activeContent === ProgressContents.ChatTools && <ChatToolsStep />}
									{activeContent === ProgressContents.Teams && <TeamsConceptStep />}
									{activeContent === ProgressContents.Integrations && <IntegrationsStep />}
									{activeContent === ProgressContents.AutomateAlertAndIncidentResponse && (
										<AutomateAlertAndIncidentResponseStep />
									)}
									{activeContent === ProgressContents.Incidents && <IncidentsStep />}
									{activeContent === ProgressContents.Heartbeats && <HeartbeatsStep />}
									{activeContent === ProgressContents.Find && <FindStep />}
									{activeContent === ProgressContents.DeprecatedOnes && <DeprecatedOnesStep />}
									<GiveFeedbackButton onClose={onClose} />
									<MinimizeButton onClose={onClose} />
								</Box>
							</>
						)}
					</Inline>
				</Modal>
			)}
		</ModalTransition>
	);
};

const stepLeftBoxStyles = xcss({
	width: '172px',
	flexShrink: 0,
});

const inlineStyles = xcss({
	height: '100%',
	position: 'relative',
});

const closeFullScreenBoxStyles = xcss({
	position: 'absolute',
	top: 'space.100',
	right: 'space.100',
});

const giveFeedbackBoxStyles = xcss({
	position: 'absolute',
	top: 'space.100',
	right: 'space.500',
});

const stepsWrapperBoxStyles = xcss({
	position: 'relative',
	height: '100%',
	width: '100%',
	overflow: 'auto',
});
